import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { PayoutPackagePicker } from '../invoice/faqtoring/PayoutPackagePicker';
import { useTranslation } from 'react-i18next';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { ORDINARY_PAYOUT, defaultPayoutChoice, packageName, payoutTermsAccepted } from '@application/features/faqtoring/models';
import { FaqtoringApi } from '@infrastructure/faqtoring-api';
const errorCode = (e) => e.response;
export function PackageDescription({ option }) {
    const title = option.displayTitle?.trim();
    return _jsxs(Box, { component: 'span', sx: { display: 'block' }, children: [_jsx(Typography, { component: 'span', fontWeight: 600, children: packageName(option) }), option.label && option.label !== packageName(option) && _jsxs(Typography, { component: 'span', color: 'text.secondary', children: [" \u00B7 ", option.label] }), title && title !== packageName(option) && _jsx(Typography, { component: 'span', variant: 'body2', sx: { display: 'block' }, children: title }), option.description && _jsx(Typography, { component: 'span', variant: 'body2', color: 'text.secondary', sx: { display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }, children: option.description }), !!option.highlights?.length && _jsx(Box, { component: 'ul', sx: { m: 0, pl: 2.5 }, children: option.highlights.map((point, i) => _jsx(Typography, { component: 'li', variant: 'body2', children: point }, i)) })] });
}
/** Profile choice between QPortal's ordinary payout and a live Faqtoring package. */
export function PayoutPreferenceSection() {
    const { t } = useTranslation('faqtoring');
    const actor = useActorBoundary();
    const [data, setData] = useState();
    const [status, setStatus] = useState('loading');
    const [choice, setChoice] = useState(ORDINARY_PAYOUT);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState();
    const sequence = useRef(0);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; sequence.current++; }; }, []);
    const apply = (next) => { setData(next); setChoice(defaultPayoutChoice(next)); setAgreed(false); };
    const load = async (keepMessage = false) => {
        const token = ++sequence.current;
        setStatus('loading');
        if (!keepMessage)
            setMessage(undefined);
        try {
            const next = await actor.read(FaqtoringApi.payoutPreference);
            if (!mounted.current || token !== sequence.current)
                return;
            apply(next);
            setStatus(next.enabled ? 'ready' : 'hidden');
        }
        catch (e) {
            if (!mounted.current || token !== sequence.current)
                return;
            // An API without this endpoint (feature not deployed) hides the section.
            setStatus(errorCode(e)?.status === 404 ? 'hidden' : 'error');
        }
    };
    useEffect(() => { void load(); }, [actor.actor]);
    if (status === 'hidden')
        return null;
    if (actor.changed)
        return _jsx(Alert, { severity: 'warning', children: t('accountChanged') });
    const blocked = !!data?.blocker;
    const needsTerms = !!data && choice !== ORDINARY_PAYOUT && !payoutTermsAccepted(data);
    const save = async () => {
        if (!data || busy || !actor.check() || (needsTerms && !agreed))
            return;
        const token = ++sequence.current;
        setBusy(true);
        setMessage(undefined);
        try {
            const ordinary = choice === ORDINARY_PAYOUT;
            const next = await FaqtoringApi.savePayoutPreference({
                route: ordinary ? 'ordinary' : 'faqtoring', serviceOptionId: ordinary ? null : choice,
                acceptedTermsVersion: needsTerms ? data.terms.version : null, expectedRevision: data.revision
            });
            if (!mounted.current || token !== sequence.current || !actor.check())
                return;
            apply(next);
            setMessage({ severity: 'success', text: t('autoPayout.saved') });
        }
        catch (e) {
            if (!mounted.current || token !== sequence.current)
                return;
            const response = errorCode(e);
            if (response?.status === 409 || response?.data?.code === 'REVISION_CONFLICT') {
                setMessage({ severity: 'warning', text: t('autoPayout.conflict') });
                setBusy(false);
                await load(true);
                return;
            }
            const code = response?.data?.code;
            setMessage({ severity: 'error', text: code ? t(`autoPayout.blockers.${code}`, { defaultValue: t('autoPayout.saveError') }) : t('autoPayout.saveError') });
        }
        finally {
            if (mounted.current && token === sequence.current)
                setBusy(false);
        }
    };
    return _jsxs("section", { "aria-labelledby": 'payout-preference-heading', children: [_jsx(Typography, { id: 'payout-preference-heading', component: 'h2', variant: 'subtitle1', sx: { fontWeight: 600, color: '#141414', mb: 1 }, children: t('autoPayout.profileTitle') }), status === 'loading' && !data && _jsxs(Box, { role: 'status', display: 'flex', gap: 1, alignItems: 'center', children: [_jsx(CircularProgress, { size: 18 }), t('autoPayout.loading')] }), status === 'error' && _jsx(Alert, { severity: 'error', action: _jsx(Button, { onClick: () => void load(), children: t('autoPayout.retry') }), children: t('autoPayout.loadError') }), data && status !== 'error' && _jsxs(Box, { "aria-busy": busy || status === 'loading', children: [!blocked && data.packageUnavailable && _jsx(Alert, { severity: 'warning', sx: { mb: 1 }, children: t('autoPayout.packageUnavailable') }), !blocked && data.termsReacceptRequired && _jsx(Alert, { severity: 'warning', sx: { mb: 1 }, children: t('autoPayout.termsReaccept') }), _jsx(PayoutPackagePicker, { preference: data, value: choice, disabled: busy, variant: 'profile', termsAgreed: agreed, onTermsAgreed: setAgreed, onChange: value => { setChoice(value); setAgreed(false); setMessage(undefined); }, onActivate: async () => { const next = await FaqtoringApi.activatePayout(); if (mounted.current && actor.check())
                            apply(next); } }), !needsTerms && choice !== ORDINARY_PAYOUT && _jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mt: 1 }, children: t('autoPayout.termsAccepted', { version: data.terms.version }) }), message && _jsx(Alert, { severity: message.severity, sx: { mt: 2 }, children: message.text }), _jsx(Button, { variant: 'contained', sx: { mt: 2 }, disabled: busy || status === 'loading' || (needsTerms && !agreed), onClick: () => void save(), children: busy ? t('autoPayout.saving') : t('autoPayout.save') })] })] });
}
